/**
 * FrankenQL Builder V2 — relationship-aware tree builder.
 *
 * Three tabs: Fields (base object fields), Lookups (related objects via lookup fields),
 * Children (child objects via LIST aggregates).
 *
 * Receives ALL synced objects (not pre-filtered). Manages its own source filter
 * (buttons per data source) and object search box. The parent passes initialSourceId
 * to pre-select the active data source button.
 *
 * When two data sources have the same object name (e.g. two Salesforce orgs both
 * have "Opportunity"), the data_source_name field disambiguates them in the list.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchSyncObjectFields,
  fetchBuilderLookups,
  fetchBuilderChildren,
  clearBuilderRelationships,
} from '../../store/slices/analyticsSlice';
import { createCustomRelationship } from '../../api/analytics';
import { buildFullQuery } from './buildFullQuery';

export { buildFullQuery };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const objKey = (obj) => obj.qualified_name || obj.name;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Search + source filter bar above the object list.
 * showSourceName: when true, object rows show data_source_name as a subtitle.
 */
function ObjectListPanel({ objects, selectedObject, onSelect, showSourceName }) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return objects;
    const q = search.toLowerCase();
    return objects.filter(o =>
      (o.label || o.name).toLowerCase().includes(q) ||
      o.name.toLowerCase().includes(q)
    );
  }, [objects, search]);

  return (
    <div className="w-48 flex-shrink-0 border-r border-gray-200 flex flex-col overflow-hidden" data-testid="builder-object-list">
      {/* Search */}
      <div className="px-2 py-1.5 border-b border-gray-100 flex-shrink-0">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter objects…"
          className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400"
          data-testid="builder-object-search"
        />
      </div>

      {/* Object list */}
      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-3 py-4 text-xs text-gray-400">
            {objects.length === 0 ? 'No objects available' : 'No matches'}
          </div>
        ) : (
          <ul>
            {filtered.map(obj => {
              const { name, label, data_source_name } = obj;
              const key = objKey(obj);
              return (
                <li key={key}>
                  <button
                    data-testid={`builder-object-${name}`}
                    onClick={() => onSelect(obj)}
                    className={`w-full text-left px-3 py-1.5 text-sm transition ${
                      name === selectedObject
                        ? 'bg-primary-50 text-primary-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="block truncate">{label || name}</span>
                    {showSourceName && data_source_name && (
                      <span className="block text-xs text-gray-400 truncate">{data_source_name}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Rotating triangle used to expand/collapse tree nodes */
function Triangle({ expanded }) {
  return (
    <svg
      className="w-3 h-3 flex-shrink-0 transition-transform duration-150"
      style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
      viewBox="0 0 10 10"
      fill="currentColor"
    >
      <polygon points="2,1 9,5 2,9" />
    </svg>
  );
}

/** A single expandable relationship row with field checkboxes below when expanded */
function RelationshipNode({ testId, label, sublabel, expanded, onToggle, fields, selectedFields, onToggleField, isLoading }) {
  return (
    <div>
      <button
        data-testid={testId ? `builder-rel-${testId}` : undefined}
        onClick={onToggle}
        className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition"
      >
        <Triangle expanded={expanded} />
        <span className="font-medium truncate">{label}</span>
        {sublabel && <span className="text-xs text-gray-400 ml-auto flex-shrink-0 truncate max-w-24">{sublabel}</span>}
      </button>
      {expanded && (
        <div className="ml-7 border-l border-gray-200 pl-2">
          {isLoading ? (
            <div className="py-2 text-xs text-gray-400">Loading fields…</div>
          ) : fields.length === 0 ? (
            <div className="py-2 text-xs text-gray-400">No fields available</div>
          ) : (
            fields.map(field => {
              const name = field.name || field;
              const checked = selectedFields.includes(name);
              return (
                <label key={name} className="flex items-center gap-2 py-0.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => onToggleField(name, !checked)}
                    className="rounded border-gray-300 text-primary-600"
                  />
                  <span className="text-xs text-gray-700 group-hover:text-gray-900 truncate">{name}</span>
                  {field.type && <span className="text-xs text-gray-400 ml-auto">{field.type}</span>}
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/** Base object fields tab */
function FieldsTab({ objectName, fields, isLoading, fieldError, selectedFields, onToggleField }) {
  if (!objectName) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-400" data-testid="builder-field-empty">
        Select an object to see its fields.
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-400" data-testid="builder-field-loading">
        Loading fields…
      </div>
    );
  }
  if (fieldError) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-red-500 px-4 text-center" data-testid="builder-field-error">
        Failed to load fields.
      </div>
    );
  }

  const handleSelectAll = () => {
    const names = fields.map(f => f.name || f);
    onToggleField(names, true, true);
  };
  const handleClear = () => onToggleField([], false, true);

  return (
    <div className="flex flex-col overflow-hidden h-full" data-testid="builder-field-panel">
      <div className="px-3 py-1.5 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
        <span className="text-xs text-gray-500">Fields ({fields.length})</span>
        <div className="flex gap-2">
          <button onClick={handleSelectAll} className="text-xs text-primary-600 hover:text-primary-800" data-testid="builder-select-all">All</button>
          <button onClick={handleClear} className="text-xs text-gray-500 hover:text-gray-700" data-testid="builder-clear-fields">Clear</button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-1 space-y-0.5">
        {fields.map(field => {
          const name = field.name || field;
          const checked = selectedFields.includes(name);
          return (
            <label key={name} className="flex items-center gap-2 cursor-pointer group py-0.5" data-testid={`builder-field-${name}`}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggleField([name], !checked)}
                className="rounded border-gray-300 text-primary-600"
              />
              <span className="text-xs text-gray-700 group-hover:text-gray-900 truncate">{name}</span>
              {field.type && <span className="text-xs text-gray-400 ml-auto flex-shrink-0">{field.type}</span>}
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** Lookups tab — shows lookup relationships as expandable tree nodes */
function LookupsTab({ lookups, isLoading, error, expandedLookups, onToggleLookup, lookupFieldStates, syncObjectFields, onToggleLookupField }) {
  const [search, setSearch] = useState('');
  if (isLoading) return <div className="p-3 text-xs text-gray-400">Loading relationships…</div>;
  if (error) return <div className="p-3 text-xs text-red-500">Failed to load lookup relationships.</div>;
  if (lookups.length === 0) return <div className="p-3 text-xs text-gray-400">No lookup relationships found for this object.</div>;

  // Cross-source lookups first, then alphabetical within each group
  const sorted = [...lookups].sort((a, b) => {
    const aX = a.data_source_id !== a.target_data_source_id ? 0 : 1;
    const bX = b.data_source_id !== b.target_data_source_id ? 0 : 1;
    if (aX !== bX) return aX - bX;
    return (a.relationship_name || a.target_object).localeCompare(b.relationship_name || b.target_object);
  });

  const q = search.toLowerCase();
  const visible = q
    ? sorted.filter(r => (r.relationship_name || r.target_object).toLowerCase().includes(q) || r.lookup_field.toLowerCase().includes(q))
    : sorted;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-2 py-1 border-b border-gray-100 flex-shrink-0">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter relationships…"
          className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {visible.map(rel => {
          const key = rel.lookup_field;
          const expanded = !!expandedLookups[key];
          const targetKey = rel.target_object;
          const fields = syncObjectFields[targetKey] || [];
          const selected = lookupFieldStates[key] || [];
          const isCrossSource = rel.data_source_id !== rel.target_data_source_id;
          return (
            <RelationshipNode
              key={key}
              testId={key}
              label={(isCrossSource ? '⚡ ' : '') + (rel.relationship_name || rel.target_object)}
              sublabel={`via ${rel.lookup_field}`}
              expanded={expanded}
              onToggle={() => onToggleLookup(rel)}
              fields={fields}
              selectedFields={selected}
              onToggleField={(fieldName, checked) => onToggleLookupField(key, fieldName, checked)}
              isLoading={expanded && fields.length === 0 && syncObjectFields[targetKey] === undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Children tab — shows child relationships as expandable tree nodes */
function ChildrenTab({ children, isLoading, error, expandedChildren, onToggleChild, childFieldStates, syncObjectFields, onToggleChildField }) {
  const [search, setSearch] = useState('');
  if (isLoading) return <div className="p-3 text-xs text-gray-400">Loading relationships…</div>;
  if (error) return <div className="p-3 text-xs text-red-500">Failed to load child relationships.</div>;
  if (children.length === 0) return <div className="p-3 text-xs text-gray-400">No child relationships found for this object.</div>;

  // Cross-source children first, then alphabetical within each group
  const sorted = [...children].sort((a, b) => {
    const aX = a.data_source_id !== a.target_data_source_id ? 0 : 1;
    const bX = b.data_source_id !== b.target_data_source_id ? 0 : 1;
    if (aX !== bX) return aX - bX;
    return a.relationship_name.localeCompare(b.relationship_name);
  });

  const q = search.toLowerCase();
  const visible = q
    ? sorted.filter(r => r.relationship_name.toLowerCase().includes(q) || r.child_object.toLowerCase().includes(q))
    : sorted;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-2 py-1 border-b border-gray-100 flex-shrink-0">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter relationships…"
          className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {visible.map(rel => {
          const key = rel.relationship_name;
          const expanded = !!expandedChildren[key];
          const childKey = rel.child_object;
          const fields = syncObjectFields[childKey] || [];
          const selected = childFieldStates[key] || [];
          const isCrossSource = rel.data_source_id !== rel.target_data_source_id;
          return (
            <RelationshipNode
              key={key}
              testId={key}
              label={(isCrossSource ? '⚡ ' : '') + rel.relationship_name}
              sublabel={`${rel.child_object}.${rel.field}`}
              expanded={expanded}
              onToggle={() => onToggleChild(rel)}
              fields={fields}
              selectedFields={selected}
              onToggleField={(fieldName, checked) => onToggleChildField(key, fieldName, checked)}
              isLoading={expanded && fields.length === 0 && syncObjectFields[childKey] === undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create Relationship Modal
// ---------------------------------------------------------------------------

/**
 * Modal for creating a custom cross-source relationship.
 * Lets the user define: child object + field → parent object + field
 * (the child has a FK column that references the parent's PK).
 */
function CreateRelationshipModal({ objects, dataSources, dsAliasMap, onClose, onCreated }) {
  const [childDsId, setChildDsId] = useState('');
  const [childObject, setChildObject] = useState('');
  const [childField, setChildField] = useState('');
  const [parentDsId, setParentDsId] = useState('');
  const [parentObject, setParentObject] = useState('');
  const [parentField, setParentField] = useState('Id');
  const [relName, setRelName] = useState('');
  const [p2cName, setP2cName] = useState('');
  const [caseInsensitive, setCaseInsensitive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const dispatch = useDispatch();
  const syncObjectFields = useSelector(state => state.analytics.syncConfig.objectFields);

  // Filtered object lists per DS
  const childObjects = useMemo(() =>
    objects ? objects.filter(o => !childDsId || o.data_source_id === childDsId) : [],
    [objects, childDsId]
  );
  const parentObjects = useMemo(() =>
    objects ? objects.filter(o => !parentDsId || o.data_source_id === parentDsId) : [],
    [objects, parentDsId]
  );

  // Load fields when object selected
  const childObjMeta = childObject ? objects?.find(o => o.name === childObject && (!childDsId || o.data_source_id === childDsId)) : null;
  const parentObjMeta = parentObject ? objects?.find(o => o.name === parentObject && (!parentDsId || o.data_source_id === parentDsId)) : null;

  useEffect(() => {
    if (childObjMeta) {
      const key = childObjMeta.qualified_name || childObjMeta.name;
      if (!syncObjectFields[key]) {
        dispatch(fetchSyncObjectFields({ objectName: childObjMeta.name, dataSourceId: childObjMeta.data_source_id, storeKey: key }));
      }
    }
  }, [childObjMeta?.qualified_name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (parentObjMeta) {
      const key = parentObjMeta.qualified_name || parentObjMeta.name;
      if (!syncObjectFields[key]) {
        dispatch(fetchSyncObjectFields({ objectName: parentObjMeta.name, dataSourceId: parentObjMeta.data_source_id, storeKey: key }));
      }
    }
  }, [parentObjMeta?.qualified_name]); // eslint-disable-line react-hooks/exhaustive-deps

  const childFieldList = childObjMeta
    ? (syncObjectFields[childObjMeta.qualified_name || childObjMeta.name] || [])
    : [];
  const parentFieldList = parentObjMeta
    ? (syncObjectFields[parentObjMeta.qualified_name || parentObjMeta.name] || [])
    : [];

  const dsLabel = (dsId) => {
    const ds = dataSources.find(d => d.id === dsId);
    if (!ds) return dsId;
    return ds.alias ? ds.alias : ds.name;
  };

  const handleSave = async () => {
    if (!childObject || !childField || !parentObject || !parentField) {
      setError('All object and field selections are required.');
      return;
    }
    if (!relName && !p2cName) {
      setError('Provide at least one relationship name.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await createCustomRelationship({
        relationship_name: relName,
        parent_to_child_name: p2cName,
        parent_data_source_id: parentDsId || null,
        parent_object: parentObject,
        parent_field: parentField,
        child_data_source_id: childDsId || null,
        child_object: childObject,
        child_field: childField,
        case_insensitive: caseInsensitive,
      });
      onCreated();
    } catch (e) {
      setError(e.message || 'Failed to create relationship.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="create-rel-modal">
      <div className="bg-white rounded-xl shadow-xl w-[520px] max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">Define Custom Relationship</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
        </div>

        <p className="text-xs text-gray-500 mb-4">
          Define how a field in one object references a field in another — even across data sources.
          <br/>
          <em>Example: HubSpot <code>companies.hs_salesforce_account_id</code> → Salesforce <code>Account.Id</code></em>
        </p>

        {/* Child (the object with the FK) */}
        <div className="mb-4">
          <div className="text-xs font-semibold text-gray-700 mb-1">Object with the foreign key (child)</div>
          <div className="flex gap-2">
            <select value={childDsId} onChange={e => { setChildDsId(e.target.value); setChildObject(''); setChildField(''); }}
              className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5">
              <option value="">Any data source</option>
              {dataSources.map(ds => <option key={ds.id} value={ds.id}>{dsLabel(ds.id)} ({ds.name})</option>)}
            </select>
            <select value={childObject} onChange={e => { setChildObject(e.target.value); setChildField(''); }}
              className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5">
              <option value="">Select object…</option>
              {childObjects.map(o => <option key={o.qualified_name || o.name} value={o.name}>{o.name}</option>)}
            </select>
          </div>
          {childObject && (
            <select value={childField} onChange={e => setChildField(e.target.value)}
              className="mt-1.5 w-full text-xs border border-gray-200 rounded px-2 py-1.5">
              <option value="">Select FK field…</option>
              {childFieldList.map(f => <option key={f.name || f} value={f.name || f}>{f.name || f}</option>)}
            </select>
          )}
        </div>

        {/* Arrow indicator */}
        <div className="text-center text-gray-400 text-sm mb-4">↓ references ↓</div>

        {/* Parent (the object being referenced) */}
        <div className="mb-4">
          <div className="text-xs font-semibold text-gray-700 mb-1">Referenced object (parent)</div>
          <div className="flex gap-2">
            <select value={parentDsId} onChange={e => { setParentDsId(e.target.value); setParentObject(''); setParentField('Id'); }}
              className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5">
              <option value="">Any data source</option>
              {dataSources.map(ds => <option key={ds.id} value={ds.id}>{dsLabel(ds.id)} ({ds.name})</option>)}
            </select>
            <select value={parentObject} onChange={e => { setParentObject(e.target.value); setParentField('Id'); }}
              className="flex-1 text-xs border border-gray-200 rounded px-2 py-1.5">
              <option value="">Select object…</option>
              {parentObjects.map(o => <option key={o.qualified_name || o.name} value={o.name}>{o.name}</option>)}
            </select>
          </div>
          {parentObject && (
            <select value={parentField} onChange={e => setParentField(e.target.value)}
              className="mt-1.5 w-full text-xs border border-gray-200 rounded px-2 py-1.5">
              <option value="Id">Id (default)</option>
              {parentFieldList.filter(f => (f.name || f) !== 'Id').map(f => <option key={f.name || f} value={f.name || f}>{f.name || f}</option>)}
            </select>
          )}
        </div>

        {/* Relationship names */}
        <div className="mb-4 grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Lookup name <span className="text-gray-400">(e.g. SFAccount)</span></label>
            <input value={relName} onChange={e => setRelName(e.target.value)} placeholder="SFAccount"
              className="w-full text-xs border border-gray-200 rounded px-2 py-1.5" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Child list name <span className="text-gray-400">(e.g. HSCompanies)</span></label>
            <input value={p2cName} onChange={e => setP2cName(e.target.value)} placeholder="HSCompanies"
              className="w-full text-xs border border-gray-200 rounded px-2 py-1.5" />
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs text-gray-600 mb-4 cursor-pointer">
          <input type="checkbox" checked={caseInsensitive} onChange={e => setCaseInsensitive(e.target.checked)}
            className="rounded border-gray-300" />
          Case-insensitive join (slower but handles mixed-case IDs)
        </label>

        {error && <div className="text-xs text-red-600 mb-3">{error}</div>}

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40">
            {saving ? 'Saving…' : 'Save relationship'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @param {{ name, label, source_type, data_source_id, data_source_name, qualified_name }[] | null} objects
 *   ALL synced objects across all sources. null = no synced objects at all.
 * @param {string|null} dataSourceId  Reset guard: resets when parent's selected source changes.
 * @param {string|null} initialSourceId  Pre-selects source filter button on mount.
 * @param {function} onQueryGenerated  (sql: string) => void
 */
export default function FrankenQLBuilder({ objects, dataSourceId, initialSourceId, onQueryGenerated }) {
  const dispatch = useDispatch();
  const syncObjectFields = useSelector(state => state.analytics.syncConfig.objectFields);
  const builderRels = useSelector(state => state.analytics.builderRelationships);
  // Redux data sources have the alias field; build a lookup map by data_source_id
  const dsAliasMap = useSelector(state =>
    Object.fromEntries((state.dataSources?.items ?? []).map(ds => [ds.data_source_id, ds.alias]))
  );

  // Source filter: which data_source_id is active (null = all sources)
  const [activeSourceId, setActiveSourceId] = useState(initialSourceId || null);

  // Base object selection
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedObjMeta, setSelectedObjMeta] = useState(null); // full object record
  const [selectedFields, setSelectedFields] = useState([]);
  const [limit, setLimit] = useState(1000);
  const [fieldError, setFieldError] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState('fields');

  // Create Relationship modal
  const [showCreateRel, setShowCreateRel] = useState(false);

  // Lookup expansion and field selection
  const [expandedLookups, setExpandedLookups] = useState({});
  const [selectedLookupFields, setSelectedLookupFields] = useState({});

  // Children expansion and field selection
  const [expandedChildren, setExpandedChildren] = useState({});
  const [selectedChildFields, setSelectedChildFields] = useState({});

  // Derive unique data sources from the objects list for the source filter buttons
  const dataSources = useMemo(() => {
    if (!objects) return [];
    const seen = new Map();
    for (const obj of objects) {
      if (obj.data_source_id && !seen.has(obj.data_source_id)) {
        seen.set(obj.data_source_id, {
          id: obj.data_source_id,
          name: obj.data_source_name || obj.source_type || obj.data_source_id.slice(0, 8),
          source_type: obj.source_type,
          alias: dsAliasMap[obj.data_source_id] || null,
        });
      }
    }
    return [...seen.values()];
  }, [objects, dsAliasMap]);

  // Objects visible in the left panel (filtered by active source button)
  const visibleObjects = useMemo(() => {
    if (!objects) return [];
    if (!activeSourceId) return objects;
    return objects.filter(o => o.data_source_id === activeSourceId);
  }, [objects, activeSourceId]);

  // Show data_source_name as subtitle when multiple sources are in play (not filtered to one)
  const showSourceName = !activeSourceId && dataSources.length > 1;

  // Reset selection state (called on source change or dataSourceId prop change)
  const resetSelection = useCallback(() => {
    setSelectedObject(null);
    setSelectedObjMeta(null);
    setSelectedFields([]);
    setFieldError(null);
    setActiveTab('fields');
    setExpandedLookups({});
    setSelectedLookupFields({});
    setExpandedChildren({});
    setSelectedChildFields({});
    dispatch(clearBuilderRelationships());
  }, [dispatch]);

  // Reset when the parent's selected data source changes
  useEffect(() => {
    if (initialSourceId !== undefined) {
      setActiveSourceId(initialSourceId || null);
    }
    resetSelection();
  }, [dataSourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Computed fields for selected object
  const storeKey = selectedObjMeta ? objKey(selectedObjMeta) : null;
  const fields = storeKey ? (syncObjectFields[storeKey] ?? null) : null;
  const isLoading = !!selectedObject && !fieldError && fields === null;
  const effectiveMeta = selectedObject && fields !== null
    ? { objects: { [selectedObject]: { fields } } }
    : null;

  const handleSourceFilter = (sourceId) => {
    setActiveSourceId(prev => prev === sourceId ? null : sourceId); // toggle: click same = all
    resetSelection();
  };

  // Select an object
  const handleSelectObject = async (obj) => {
    const { name } = obj;
    setSelectedObject(name);
    setSelectedObjMeta(obj);
    setSelectedFields([]);
    setFieldError(null);
    setActiveTab('fields');
    setExpandedLookups({});
    setSelectedLookupFields({});
    setExpandedChildren({});
    setSelectedChildFields({});
    dispatch(clearBuilderRelationships());

    const key = objKey(obj);

    // Load base fields if not cached
    if (!syncObjectFields[key]) {
      try {
        await dispatch(fetchSyncObjectFields({
          objectName: name,
          dataSourceId: obj.data_source_id || null,
          storeKey: key,
        })).unwrap();
      } catch {
        setFieldError(name);
      }
    }

    // Load relationships in parallel
    dispatch(fetchBuilderLookups({ objectName: name }));
    dispatch(fetchBuilderChildren({ objectName: name }));
  };

  // Toggle base fields
  const handleToggleField = (names, checked, bulk = false) => {
    if (bulk) {
      setSelectedFields(checked ? names : []);
    } else if (names.length > 1) {
      setSelectedFields(names);
    } else {
      const [n] = names;
      setSelectedFields(prev => checked ? [...prev, n] : prev.filter(f => f !== n));
    }
  };

  // Lookup tree node expansion
  const handleToggleLookup = useCallback(async (rel) => {
    const key = rel.lookup_field;
    const targetKey = rel.target_object;
    const wasExpanded = expandedLookups[key];
    setExpandedLookups(prev => ({ ...prev, [key]: !wasExpanded }));
    if (!wasExpanded && syncObjectFields[targetKey] === undefined) {
      // For cross-source lookups use the target DS; for same-DS use source DS
      const targetDsId = rel.target_data_source_id || rel.data_source_id || activeSourceId || dataSourceId;
      dispatch(fetchSyncObjectFields({
        objectName: rel.target_object,
        dataSourceId: targetDsId,
        storeKey: targetKey,
      }));
    }
  }, [expandedLookups, syncObjectFields, dispatch, activeSourceId, dataSourceId]);

  const handleToggleLookupField = (lookupField, fieldName, checked) => {
    setSelectedLookupFields(prev => {
      const current = prev[lookupField] || [];
      return {
        ...prev,
        [lookupField]: checked ? [...current, fieldName] : current.filter(f => f !== fieldName),
      };
    });
  };

  // Child tree node expansion
  const handleToggleChild = useCallback(async (rel) => {
    const key = rel.relationship_name;
    const childKey = rel.child_object;
    const wasExpanded = expandedChildren[key];
    setExpandedChildren(prev => ({ ...prev, [key]: !wasExpanded }));
    if (!wasExpanded && syncObjectFields[childKey] === undefined) {
      // For cross-source children use the target DS; for same-DS use source DS
      const targetDsId = rel.target_data_source_id || rel.data_source_id || activeSourceId || dataSourceId;
      dispatch(fetchSyncObjectFields({
        objectName: rel.child_object,
        dataSourceId: targetDsId,
        storeKey: childKey,
      }));
    }
  }, [expandedChildren, syncObjectFields, dispatch, activeSourceId, dataSourceId]);

  const handleToggleChildField = (relName, fieldName, checked) => {
    setSelectedChildFields(prev => {
      const current = prev[relName] || [];
      return {
        ...prev,
        [relName]: checked ? [...current, fieldName] : current.filter(f => f !== fieldName),
      };
    });
  };

  // Build SQL inputs
  const lookupSelections = {};
  for (const rel of builderRels.lookup) {
    const key = rel.lookup_field;
    const selected = selectedLookupFields[key];
    if (selected && selected.length > 0) {
      lookupSelections[key] = {
        targetObject: rel.target_object,
        targetField: rel.target_field || null,
        selectedFields: selected,
        // Store target DS id for alias derivation in handleApply
        _targetDataSourceId: rel.target_data_source_id || null,
      };
    }
  }

  const childSelections = {};
  for (const rel of builderRels.children) {
    const key = rel.relationship_name;
    const selected = selectedChildFields[key];
    if (selected && selected.length > 0) {
      childSelections[key] = {
        childObject: rel.child_object,
        joinField: rel.field,
        selectedFields: selected,
        _childDataSourceId: rel.target_data_source_id || rel.data_source_id || null,
      };
    }
  }

  const canApply = !!selectedObject && !isLoading && !fieldError && !!effectiveMeta;

  const handleApply = () => {
    if (!canApply) return;
    const baseDs = selectedObjMeta?.data_source_id;
    const baseAlias = baseDs ? (dsAliasMap[baseDs] ?? null) : null;

    // Annotate each lookup with its data source alias (target DS for cross-source joins)
    const lookupSelectionsWithAlias = Object.fromEntries(
      Object.entries(lookupSelections).map(([field, sel]) => {
        // Use the target DS id stored in sel, falling back to base DS for same-source lookups
        const targetDsId = sel._targetDataSourceId || baseDs;
        const { _targetDataSourceId: _removed, ...rest } = sel;
        return [field, { ...rest, sourceAlias: targetDsId ? (dsAliasMap[targetDsId] ?? null) : null }];
      })
    );

    const childSelectionsWithAlias = Object.fromEntries(
      Object.entries(childSelections).map(([relName, sel]) => {
        const childDsId = sel._childDataSourceId || baseDs;
        const { _childDataSourceId: _removed, ...rest } = sel;
        return [relName, { ...rest, childDsAlias: childDsId ? (dsAliasMap[childDsId] ?? null) : null }];
      })
    );

    const { sql } = buildFullQuery({
      baseObject: selectedObject,
      baseAlias,
      baseFields: selectedFields,
      lookupSelections: lookupSelectionsWithAlias,
      childSelections: childSelectionsWithAlias,
      limit,
    });
    onQueryGenerated(sql);
    resetSelection();
  };

  // Tab badge counts
  const lookupCount = Object.values(selectedLookupFields).filter(a => a.length > 0).length;
  const childCount = Object.values(selectedChildFields).filter(a => a.length > 0).length;

  if (objects === null) {
    return (
      <div className="flex items-center justify-center h-20 text-sm text-gray-400" data-testid="frankenql-builder">
        No synced objects yet. Add objects in the Sync Config tab.
      </div>
    );
  }

  const handleRelCreated = useCallback(() => {
    setShowCreateRel(false);
    // Refresh relationships for the selected object if any
    if (selectedObject) {
      dispatch(fetchBuilderLookups({ objectName: selectedObject }));
      dispatch(fetchBuilderChildren({ objectName: selectedObject }));
    }
  }, [selectedObject, dispatch]);

  return (
    <>
    {showCreateRel && (
      <CreateRelationshipModal
        objects={objects}
        dataSources={dataSources}
        dsAliasMap={dsAliasMap}
        onClose={() => setShowCreateRel(false)}
        onCreated={handleRelCreated}
      />
    )}
    <div className="flex flex-col" style={{ height: '300px' }} data-testid="frankenql-builder">
      {/* Header: source filter buttons + limit + apply */}
      <div className="px-3 py-2 border-b border-gray-200 flex items-center gap-2 bg-gray-50 flex-shrink-0 flex-wrap">
        {/* Source filter buttons */}
        {dataSources.length > 1 && (
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={() => { setActiveSourceId(null); resetSelection(); }}
              className={`px-2 py-0.5 rounded text-xs font-medium transition ${
                !activeSourceId
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              data-testid="builder-source-all"
            >
              All
            </button>
            {dataSources.map(ds => (
              <button
                key={ds.id}
                onClick={() => handleSourceFilter(ds.id)}
                title={ds.id}
                className={`px-2 py-0.5 rounded text-xs font-medium transition max-w-28 truncate ${
                  activeSourceId === ds.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                data-testid={`builder-source-${ds.id}`}
              >
                {ds.alias ? <code>{ds.alias}</code> : ds.name}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={() => setShowCreateRel(true)}
            className="px-2 py-1 text-xs text-primary-600 border border-primary-200 rounded hover:bg-primary-50 transition"
            data-testid="builder-create-rel-btn"
            title="Define a custom cross-source relationship"
          >
            + Relationship
          </button>
          <span className="text-xs text-gray-500">LIMIT</span>
          <input
            type="number"
            value={limit}
            onChange={e => setLimit(e.target.value)}
            min={1}
            max={10000}
            className="w-16 text-xs border border-gray-200 rounded px-1.5 py-1"
            data-testid="builder-limit-input"
          />
          <button
            data-testid="builder-apply-btn"
            onClick={handleApply}
            disabled={!canApply}
            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Apply ▶
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Object list with search */}
        <ObjectListPanel
          objects={visibleObjects}
          selectedObject={selectedObject}
          onSelect={handleSelectObject}
          showSourceName={showSourceName}
        />

        {/* Right panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {selectedObject ? (
            <>
              {/* Tabs */}
              <div className="flex border-b border-gray-200 flex-shrink-0 bg-gray-50">
                {[
                  { key: 'fields', label: `Fields${selectedFields.length > 0 ? ` (${selectedFields.length})` : ''}` },
                  { key: 'lookups', label: `Lookups${lookupCount > 0 ? ` (${lookupCount})` : ''}` },
                  { key: 'children', label: `Children${childCount > 0 ? ` (${childCount})` : ''}` },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-3 py-1.5 text-xs font-medium transition border-b-2 ${
                      activeTab === tab.key
                        ? 'border-primary-500 text-primary-700 bg-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={`builder-tab-${tab.key}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="flex-1 overflow-hidden">
                {activeTab === 'fields' && (
                  <FieldsTab
                    objectName={selectedObject}
                    fields={fields || []}
                    isLoading={isLoading}
                    fieldError={fieldError === selectedObject}
                    selectedFields={selectedFields}
                    onToggleField={handleToggleField}
                  />
                )}
                {activeTab === 'lookups' && (
                  <LookupsTab
                    lookups={builderRels.lookup}
                    isLoading={builderRels.isLoading}
                    error={builderRels.error}
                    expandedLookups={expandedLookups}
                    onToggleLookup={handleToggleLookup}
                    lookupFieldStates={selectedLookupFields}
                    syncObjectFields={syncObjectFields}
                    onToggleLookupField={handleToggleLookupField}
                  />
                )}
                {activeTab === 'children' && (
                  <ChildrenTab
                    children={builderRels.children}
                    isLoading={builderRels.isLoading}
                    error={builderRels.error}
                    expandedChildren={expandedChildren}
                    onToggleChild={handleToggleChild}
                    childFieldStates={selectedChildFields}
                    syncObjectFields={syncObjectFields}
                    onToggleChildField={handleToggleChildField}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-400" data-testid="builder-field-empty">
              Select an object to see its fields.
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
